import { matchPath } from 'react-router-dom'
import { logger } from '@/lib/utils/logger'

export type RouteDescriptor = {
  title: string
  primaryActions: string[]
}

const IN_APP_ROUTE_ALLOWLIST = [
  '/dashboard',
  '/dashboard/home',
  '/dashboard/assistants',
  '/dashboard/assistants/agent/:encodedBotId',
  '/dashboard/reports',
  '/dashboard/integrations',
  '/dashboard/settings',
  '/help',
  '/nursing',
] as const

const ROUTE_DESCRIPTORS: Array<{
  pattern: string
  descriptor: RouteDescriptor
}> = [
  {
    pattern: '/dashboard',
    descriptor: {
      title: 'Home dashboard',
      primaryActions: ['review account highlights', 'open assistants', 'move into core product areas'],
    },
  },
  {
    pattern: '/dashboard/home',
    descriptor: {
      title: 'Home dashboard',
      primaryActions: ['review account highlights', 'open assistants', 'move into core product areas'],
    },
  },
  {
    pattern: '/dashboard/assistants',
    descriptor: {
      title: 'Assistants library',
      primaryActions: ['browse assistants', 'open an assistant console', 'compare available assistants'],
    },
  },
  {
    pattern: '/dashboard/assistants/agent/:encodedBotId',
    descriptor: {
      title: 'Assistant console',
      primaryActions: ['talk with the active assistant', 'review transcript activity', 'monitor tool progress'],
    },
  },
  {
    pattern: '/dashboard/reports',
    descriptor: {
      title: 'Reports',
      primaryActions: ['review charts', 'inspect performance details', 'change report filters'],
    },
  },
  {
    pattern: '/dashboard/integrations',
    descriptor: {
      title: 'Integrations',
      primaryActions: ['review connected services', 'start an integration setup flow', 'inspect provider status'],
    },
  },
  {
    pattern: '/dashboard/settings',
    descriptor: {
      title: 'Settings',
      primaryActions: ['manage profile details', 'review API keys', 'update account configuration'],
    },
  },
  {
    pattern: '/help',
    descriptor: {
      title: 'Help center',
      primaryActions: ['search documentation', 'review support guidance', 'open product help content'],
    },
  },
  {
    pattern: '/nursing',
    descriptor: {
      title: 'Nursing information',
      primaryActions: ['review nursing-specific content', 'continue guided product onboarding', 'inspect educational material'],
    },
  },
]

type AppPreference = 'auto' | 'google_maps' | 'waze' | 'browser'
type NavigationProvider = 'google_maps' | 'waze'
export type PhoneDialerLaunchMode = 'blank_page' | 'gesture_prompt'

export type PhoneDialerRequest = {
  telUrl: string
  displayNumber: string
  contactName?: string
  reason?: string
}

type PhoneDialerTarget = PhoneDialerRequest | { error: string }

const PHONE_DIALER_LAUNCH_MODE_STORAGE_KEY = 'botnexus:phone-dialer-launch-mode'

function normalizeOriginPath(value: string): string | null {
  try {
    const parsed = new URL(value, window.location.origin)
    if (parsed.origin !== window.location.origin) return null
    return `${parsed.pathname}${parsed.search}${parsed.hash}`
  } catch {
    return null
  }
}

function readPathParam(params: Record<string, unknown>): string | null {
  const rawPath =
    (typeof params.path === 'string' && params.path.trim()) ||
    (typeof params.destination === 'string' && params.destination.trim()) ||
    (typeof params.url === 'string' && params.url.trim()) ||
    (typeof params.href === 'string' && params.href.trim())

  if (!rawPath) return null
  return normalizeOriginPath(rawPath)
}

function normalizePathname(path: string): string {
  try {
    return new URL(path, window.location.origin).pathname
  } catch {
    return path
  }
}

function isNavigationCapableWebUrl(parsed: URL): boolean {
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
  const host = parsed.hostname.toLowerCase()
  const path = parsed.pathname.toLowerCase()
  const isGoogleMapsHost =
    host === 'maps.google.com' ||
    host === 'google.com' ||
    host === 'www.google.com' ||
    host.endsWith('.google.com') ||
    host === 'maps.app.goo.gl'
  const isWazeHost = host === 'waze.com' || host === 'www.waze.com' || host.endsWith('.waze.com')
  if (isWazeHost) return true
  if (host === 'maps.app.goo.gl') return true
  return isGoogleMapsHost && path.includes('/maps')
}

function inferNavigationProvider(parsed: URL): NavigationProvider | null {
  const protocol = parsed.protocol.toLowerCase()
  if (protocol === 'google.navigation:' || protocol === 'comgooglemaps:' || protocol === 'geo:') {
    return 'google_maps'
  }
  if (protocol === 'waze:') return 'waze'
  if (parsed.protocol === 'http:' || parsed.protocol === 'https:') {
    const host = parsed.hostname.toLowerCase()
    if (host === 'waze.com' || host === 'www.waze.com' || host.endsWith('.waze.com')) return 'waze'
    if (isNavigationCapableWebUrl(parsed)) return 'google_maps'
  }
  return null
}

function buildGoogleMapsDeepLink(parsed: URL, normalizedUrl: string): string | null {
  const protocol = parsed.protocol.toLowerCase()
  if (protocol === 'google.navigation:' || protocol === 'comgooglemaps:') return normalizedUrl
  if (protocol === 'geo:') return `google.navigation:${parsed.pathname}${parsed.search}`
  if (protocol !== 'http:' && protocol !== 'https:') return null

  const destination =
    parsed.searchParams.get('destination') ||
    parsed.searchParams.get('q') ||
    parsed.searchParams.get('query') ||
    parsed.searchParams.get('daddr')

  if (!destination) return null
  return `comgooglemaps://?daddr=${encodeURIComponent(destination)}`
}

function buildWazeDeepLink(parsed: URL, normalizedUrl: string): string | null {
  const protocol = parsed.protocol.toLowerCase()
  if (protocol === 'waze:') return normalizedUrl
  if (protocol !== 'http:' && protocol !== 'https:') return null

  const ll = parsed.searchParams.get('ll')
  const query = parsed.searchParams.get('q') || parsed.searchParams.get('destination')
  if (ll) return `waze://?ll=${encodeURIComponent(ll)}&navigate=yes`
  if (query) return `waze://?q=${encodeURIComponent(query)}&navigate=yes`
  return null
}

function parseBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value
  if (typeof value !== 'string') return false
  const normalized = value.trim().toLowerCase()
  return normalized === 'true' || normalized === '1' || normalized === 'yes'
}

function parseAppPreference(value: unknown): AppPreference {
  if (typeof value !== 'string') return 'auto'
  const normalized = value.trim().toLowerCase()
  if (normalized === 'google_maps' || normalized === 'waze' || normalized === 'browser') {
    return normalized
  }
  return 'auto'
}

function maskPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (!digits) return '[empty]'
  return `***${digits.slice(-4)}`
}

function readOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

export function readPhoneDialerLaunchMode(): PhoneDialerLaunchMode {
  const value = localStorage.getItem(PHONE_DIALER_LAUNCH_MODE_STORAGE_KEY)
  return value === 'gesture_prompt' ? 'gesture_prompt' : 'blank_page'
}

export function writePhoneDialerLaunchMode(mode: PhoneDialerLaunchMode) {
  localStorage.setItem(PHONE_DIALER_LAUNCH_MODE_STORAGE_KEY, mode)
}

function stripTelScheme(value: string): { phoneValue: string } | { error: string } {
  const schemeMatch = value.match(/^([a-z][a-z0-9+.-]*):/i)
  if (!schemeMatch) return { phoneValue: value }

  if (schemeMatch[1].toLowerCase() !== 'tel') {
    return { error: 'Error: Unsupported phone URL protocol.' }
  }

  let phoneValue = value.slice(schemeMatch[0].length)
  if (phoneValue.startsWith('//')) {
    phoneValue = phoneValue.slice(2)
  }
  return { phoneValue }
}

function normalizePhoneDialerTarget(rawValue: string): PhoneDialerTarget {
  const stripped = stripTelScheme(rawValue.trim())
  if ('error' in stripped) return stripped

  let phoneValue = stripped.phoneValue.trim()
  try {
    phoneValue = decodeURIComponent(phoneValue)
  } catch {
    return { error: 'Error: Invalid phone number.' }
  }

  if (!phoneValue || phoneValue.includes('?') || phoneValue.includes('#') || phoneValue.includes(';')) {
    return { error: 'Error: Invalid phone number.' }
  }

  const compactValue = phoneValue.replace(/[\s().-]/g, '')
  if (!/^\+?\d+$/.test(compactValue)) {
    return { error: 'Error: Invalid phone number.' }
  }

  const digits = compactValue.replace(/\D/g, '')
  if (digits.length < 7 || digits.length > 15) {
    return { error: 'Error: Invalid phone number.' }
  }

  let displayNumber = compactValue.startsWith('+') ? `+${digits}` : digits
  if (!compactValue.startsWith('+') && digits.length === 10) {
    displayNumber = `+1${digits}`
  } else if (!compactValue.startsWith('+') && digits.length === 11 && digits.startsWith('1')) {
    displayNumber = `+${digits}`
  }

  return {
    telUrl: `tel:${displayNumber}`,
    displayNumber,
  }
}

function requestExternalProtocolOpen(url: string): void {
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.target = '_blank'
  anchor.rel = 'noopener noreferrer'
  anchor.style.display = 'none'

  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
}

function buildPreferredNavigationDeepLink(
  parsed: URL,
  normalizedUrl: string,
  preference: AppPreference
): string | null {
  const provider =
    preference === 'auto'
      ? inferNavigationProvider(parsed)
      : preference === 'browser'
        ? null
        : preference

  if (!provider) return null
  return provider === 'google_maps'
    ? buildGoogleMapsDeepLink(parsed, normalizedUrl)
    : buildWazeDeepLink(parsed, normalizedUrl)
}

function buildMobileAppDeepLink(parsed: URL): string | null {
  const ua = navigator.userAgent
  const isIOS = /iPhone|iPad|iPod/i.test(ua)
  const isAndroid = /Android/i.test(ua)
  const isMobileUA = isIOS || isAndroid
  if (!isMobileUA) return null

  const host = parsed.hostname.toLowerCase()

  if (['youtube.com', 'www.youtube.com', 'm.youtube.com'].includes(host)) {
    const videoId = parsed.searchParams.get('v')
    if (videoId) {
      if (isIOS) return `youtube://watch?v=${videoId}`
      if (isAndroid) return `intent://www.youtube.com/watch?v=${videoId}#Intent;scheme=https;package=com.google.android.youtube;end`
    }
    if (isIOS) return `youtube:/${parsed.pathname}${parsed.search}`
    if (isAndroid) return `intent:/${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.google.android.youtube;end`
  }

  if (host === 'youtu.be') {
    const videoId = parsed.pathname.slice(1)
    if (videoId) {
      if (isIOS) return `youtube://watch?v=${videoId}`
      if (isAndroid) return `intent://www.youtube.com/watch?v=${videoId}#Intent;scheme=https;package=com.google.android.youtube;end`
    }
  }

  if (host === 'open.spotify.com') {
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parts.length >= 2) return `spotify:${parts.join(':')}`
  }

  if (host === 'instagram.com' || host === 'www.instagram.com') {
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parts.length === 1) {
      if (isIOS) return `instagram://user?username=${parts[0]}`
      if (isAndroid) return `intent://www.instagram.com/${parts[0]}#Intent;scheme=https;package=com.instagram.android;end`
    }
    if (isAndroid) {
      return `intent://www.instagram.com${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.instagram.android;end`
    }
  }

  if (['twitter.com', 'www.twitter.com', 'x.com', 'www.x.com'].includes(host)) {
    const parts = parsed.pathname.split('/').filter(Boolean)
    if (parts.length === 1 && isIOS) return `twitter://user?screen_name=${parts[0]}`
    if (parts.length >= 3 && parts[1] === 'status' && isIOS) return `twitter://status?id=${parts[2]}`
    if (isAndroid) return `intent://${host}${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.twitter.android;end`
  }

  if (['tiktok.com', 'www.tiktok.com', 'vm.tiktok.com'].includes(host) && /Android/i.test(ua)) {
    return `intent://${host}${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.zhiliaoapp.musically;end`
  }

  if (['facebook.com', 'www.facebook.com', 'm.facebook.com', 'fb.com'].includes(host)) {
    if (isIOS) return `fb://facewebmodal/f?href=${encodeURIComponent(parsed.toString())}`
    if (isAndroid) return `intent://www.facebook.com${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.facebook.katana;end`
  }

  if ((host === 'linkedin.com' || host === 'www.linkedin.com') && /Android/i.test(ua)) {
    return `intent://www.linkedin.com${parsed.pathname}${parsed.search}#Intent;scheme=https;package=com.linkedin.android;end`
  }

  return null
}

export function isAssistantRoute(pathname: string): boolean {
  return !!matchPath('/dashboard/assistants/agent/:encodedBotId', pathname)
}

export function isAllowedInAppPath(path: string): boolean {
  const pathname = normalizePathname(path)
  return IN_APP_ROUTE_ALLOWLIST.some((pattern) =>
    !!matchPath({ path: pattern, end: true }, pathname)
  )
}

export function buildRouteContextDescription(pathname: string): string {
  const match = ROUTE_DESCRIPTORS.find(({ pattern }) =>
    !!matchPath({ path: pattern, end: true }, pathname)
  )

  const descriptor = match?.descriptor ?? {
    title: 'BotNexus application page',
    primaryActions: ['review the current page context', 'guide the user using allowlisted routes', 'continue the active task'],
  }

  return [
    `The user is now on ${pathname}.`,
    `This page is the ${descriptor.title}.`,
    `Primary actions here: ${descriptor.primaryActions.join(', ')}.`,
  ].join('\n')
}

export function openNavigationInNewWindow(params: Record<string, unknown>): string {
  const rawUrl =
    (typeof params.url === 'string' && params.url.trim()) ||
    (typeof params.href === 'string' && params.href.trim()) ||
    (typeof params.link === 'string' && params.link.trim())

  if (!rawUrl) {
    return 'Error: Missing required `url` parameter.'
  }

  const normalizedUrl = rawUrl.trim()
  const lowerUrl = normalizedUrl.toLowerCase()
  if (lowerUrl.startsWith('javascript:') || lowerUrl.startsWith('data:')) {
    return 'Error: Unsupported URL protocol.'
  }

  const allowedProtocols = new Set([
    'http:',
    'https:',
    'geo:',
    'google.navigation:',
    'waze:',
    'comgooglemaps:',
  ])

  let parsedPrimaryUrl: URL
  try {
    parsedPrimaryUrl = new URL(normalizedUrl, window.location.origin)
    if (!allowedProtocols.has(parsedPrimaryUrl.protocol.toLowerCase())) {
      return `Error: Unsupported URL protocol: ${parsedPrimaryUrl.protocol}`
    }
  } catch {
    return 'Error: Invalid URL.'
  }

  const protocol = parsedPrimaryUrl.protocol.toLowerCase()
  const isNavigationCapable =
    protocol === 'geo:' ||
    protocol === 'google.navigation:' ||
    protocol === 'waze:' ||
    protocol === 'comgooglemaps:' ||
    isNavigationCapableWebUrl(parsedPrimaryUrl)

  const rawFallbackUrl = typeof params.fallback_url === 'string' ? params.fallback_url.trim() : ''
  if (rawFallbackUrl) {
    const lowerFallback = rawFallbackUrl.toLowerCase()
    if (lowerFallback.startsWith('javascript:') || lowerFallback.startsWith('data:')) {
      return 'Error: Unsupported fallback_url protocol.'
    }

    try {
      const parsedFallback = new URL(rawFallbackUrl, window.location.origin)
      const fallbackProtocol = parsedFallback.protocol.toLowerCase()
      if (fallbackProtocol !== 'http:' && fallbackProtocol !== 'https:') {
        return `Error: Unsupported fallback_url protocol: ${parsedFallback.protocol}`
      }
    } catch {
      return 'Error: Invalid fallback_url.'
    }
  }

  const preferNativeNavigationApp = parseBoolean(params.prefer_native_navigation_app)
  const appPreference = parseAppPreference(params.app_preference)
  const preferredDeepLink =
    isNavigationCapable && preferNativeNavigationApp && appPreference !== 'browser'
      ? buildPreferredNavigationDeepLink(parsedPrimaryUrl, normalizedUrl, appPreference)
      : null
  const mobileAppDeepLink = preferredDeepLink ? null : buildMobileAppDeepLink(parsedPrimaryUrl)
  const targetUrl = preferredDeepLink ?? mobileAppDeepLink ?? normalizedUrl

  window.open(targetUrl, '_blank', 'noopener,noreferrer')

  logger.info('[AssistantRuntime] External navigation opened from client tool', {
    url: normalizedUrl,
    openedUrl: targetUrl,
    preferNativeNavigationApp,
    appPreference,
    usedMobileAppDeepLink: !!mobileAppDeepLink,
  })

  return `Navigation opened successfully: ${targetUrl}`
}

export function createOpenPhoneDialerTool(options: {
  onDialerRequested: (request: PhoneDialerRequest) => void
  getLaunchMode?: () => PhoneDialerLaunchMode
}) {
  return (params: Record<string, unknown>): string => {
    const rawPhoneNumber = typeof params.phone_number === 'string' ? params.phone_number.trim() : ''

    if (!rawPhoneNumber) {
      return 'Error: Missing required `phone_number` parameter.'
    }

    const target = normalizePhoneDialerTarget(rawPhoneNumber)
    if ('error' in target) {
      logger.warn('[AssistantRuntime] Phone dialer request rejected from client tool', {
        phoneNumber: maskPhoneNumber(rawPhoneNumber),
        error: target.error,
      })
      return target.error
    }

    const request: PhoneDialerRequest = {
      ...target,
      contactName: readOptionalString(params.contact_name),
      reason: readOptionalString(params.reason),
    }

    const launchMode = options.getLaunchMode?.() ?? 'blank_page'
    if (launchMode === 'blank_page') {
      requestExternalProtocolOpen(request.telUrl)

      logger.info('[AssistantRuntime] Phone dialer blank-page open requested from client tool', {
        phoneNumber: maskPhoneNumber(target.displayNumber),
        hasContactName: !!request.contactName,
        hasReason: !!request.reason,
      })

      return `Phone dialer open requested for ${target.displayNumber}. If nothing opens, ask the user to switch Dialer launch to Gesture fallback in settings.`
    }

    options.onDialerRequested(request)

    logger.info('[AssistantRuntime] Phone dialer prompt requested from client tool', {
      phoneNumber: maskPhoneNumber(target.displayNumber),
      hasContactName: !!request.contactName,
      hasReason: !!request.reason,
    })

    return `Phone dialer is ready for ${target.displayNumber}. Ask the user to tap the Open dialer button.`
  }
}

export function createNavigateInAppTool(options: {
  navigate: (path: string) => void
  onNavigated?: (path: string) => void
}) {
  return (params: Record<string, unknown>): string => {
    const path = readPathParam(params)
    if (!path) {
      return 'Error: Missing required `path` parameter.'
    }

    if (!isAllowedInAppPath(path)) {
      return `Error: Path is not allowlisted for in-app navigation: ${path}`
    }

    options.onNavigated?.(path)
    options.navigate(path)
    return `Navigation completed in app: ${path}`
  }
}
